/**
 * BookingsManagement - Manage accommodation bookings
 */
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  Calendar, Plus, Search, LogIn, LogOut, XCircle,
  User, BedDouble, Clock, DollarSign, Filter
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Textarea } from '../ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { toast } from 'sonner';
import { getToken } from '@/lib/authToken';
import SmartRoomPicker from './SmartRoomPicker';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const statusColors = {
  confirmed: 'bg-yellow-100 text-yellow-800',
  checked_in: 'bg-green-100 text-green-800',
  checked_out: 'bg-gray-100 text-gray-800',
  cancelled: 'bg-red-100 text-red-800'
};

const BookingsManagement = () => {
  const queryClient = useQueryClient();
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [activeView, setActiveView] = useState('active');
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [actionType, setActionType] = useState(null); // 'checkin', 'checkout', 'cancel'

  const token = getToken();
  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  // Fetch rooms
  const { data: rooms = [] } = useQuery({
    queryKey: ['accommodation-rooms'],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/admin/accommodation/rooms`, { headers });
      if (!res.ok) return [];
      return res.json();
    }
  });

  // Fetch guests
  const { data: guests = [] } = useQuery({
    queryKey: ['accommodation-guests'],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/admin/accommodation/guests`, { headers });
      if (!res.ok) return [];
      return res.json();
    }
  });

  // Fetch bookings
  const { data: bookings = [], isLoading } = useQuery({
    queryKey: ['accommodation-bookings', activeView],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/admin/accommodation/bookings?status=${activeView}`, { headers });
      if (!res.ok) return [];
      return res.json();
    }
  });

  // Create booking
  const createMutation = useMutation({
    mutationFn: async (data) => {
      const res = await fetch(`${API_URL}/api/admin/accommodation/bookings`, {
        method: 'POST',
        headers,
        body: JSON.stringify(data)
      });
      if (!res.ok) throw new Error((await res.json()).detail || 'Failed to create booking');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['accommodation-bookings']);
      queryClient.invalidateQueries(['accommodation-rooms']);
      queryClient.invalidateQueries(['accommodation-stats']);
      setShowAddDialog(false);
      toast.success('Booking created');
    },
    onError: (error) => toast.error(error.message)
  });

  // Check-in
  const checkInMutation = useMutation({
    mutationFn: async (bookingId) => {
      const res = await fetch(`${API_URL}/api/admin/accommodation/bookings/${bookingId}/check-in`, {
        method: 'POST',
        headers
      });
      if (!res.ok) throw new Error((await res.json()).detail || 'Failed to check in');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['accommodation-bookings']);
      queryClient.invalidateQueries(['accommodation-rooms']);
      queryClient.invalidateQueries(['accommodation-stats']);
      setSelectedBooking(null);
      setActionType(null);
      toast.success('Guest checked in');
    },
    onError: (error) => toast.error(error.message)
  });

  // Check-out
  const checkOutMutation = useMutation({
    mutationFn: async ({ bookingId, roomStatus }) => {
      const res = await fetch(`${API_URL}/api/admin/accommodation/bookings/${bookingId}/check-out`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ room_status: roomStatus })
      });
      if (!res.ok) throw new Error((await res.json()).detail || 'Failed to check out');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['accommodation-bookings']);
      queryClient.invalidateQueries(['accommodation-rooms']);
      queryClient.invalidateQueries(['accommodation-stats']);
      setSelectedBooking(null);
      setActionType(null);
      toast.success('Guest checked out');
    },
    onError: (error) => toast.error(error.message)
  });

  // Cancel booking
  const cancelMutation = useMutation({
    mutationFn: async ({ bookingId, reason }) => {
      const res = await fetch(`${API_URL}/api/admin/accommodation/bookings/${bookingId}/cancel`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ reason })
      });
      if (!res.ok) throw new Error((await res.json()).detail || 'Failed to cancel booking');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['accommodation-bookings']);
      queryClient.invalidateQueries(['accommodation-stats']);
      setSelectedBooking(null);
      setActionType(null);
      toast.success('Booking cancelled');
    },
    onError: (error) => toast.error(error.message)
  });

  const availableRooms = rooms.filter(r => r.status === 'ready');

  const BookingForm = ({ onSubmit, onCancel, isLoading }) => {
    const [formData, setFormData] = useState({
      room_id: '',
      guest_id: '',
      check_in_date: new Date().toISOString().split('T')[0],
      check_out_date: '',
      purpose: 'accommodation',
      notes: '',
      total_amount: 0,
      payment_status: 'pending'
    });

    const [useNewGuest, setUseNewGuest] = useState(false);
    const [newGuest, setNewGuest] = useState({
      full_name: '',
      phone: '',
      id_number: ''
    });

    const selectedRoom = rooms.find(r => r.id === formData.room_id);

    return (
      <form
        onSubmit={(e) => {
          e.preventDefault();
          const data = { ...formData };
          if (useNewGuest) {
            data.guest_data = {
              full_name: newGuest.full_name,
              phone: newGuest.phone,
              id_number: newGuest.id_number
            };
            delete data.guest_id;
          }
          onSubmit(data);
        }}
        className="space-y-4"
      >
        <div>
          <label className="text-sm font-medium">Room *</label>
          <SmartRoomPicker
            value={formData.room_id}
            onChange={(roomId) => setFormData({ ...formData, room_id: roomId })}
            placeholder="Search rooms by number, building, floor or capacity…"
          />
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-sm font-medium">Guest *</label>
            <Button 
              type="button" 
              variant="link" 
              size="sm"
              onClick={() => setUseNewGuest(!useNewGuest)}
            >
              {useNewGuest ? 'Select existing guest' : 'Create new guest'}
            </Button>
          </div>
          
          {useNewGuest ? (
            <div className="space-y-3 p-3 bg-gray-50 rounded-lg">
              <Input
                placeholder="Full name *"
                value={newGuest.full_name}
                onChange={(e) => setNewGuest({ ...newGuest, full_name: e.target.value })}
                required
              />
              <div className="grid grid-cols-2 gap-2">
                <Input
                  placeholder="Phone"
                  value={newGuest.phone}
                  onChange={(e) => setNewGuest({ ...newGuest, phone: e.target.value })}
                />
                <Input
                  placeholder="ID Number"
                  value={newGuest.id_number}
                  onChange={(e) => setNewGuest({ ...newGuest, id_number: e.target.value })}
                />
              </div>
            </div>
          ) : (
            <Select
              value={formData.guest_id}
              onValueChange={(value) => setFormData({ ...formData, guest_id: value })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select guest" />
              </SelectTrigger>
              <SelectContent>
                {guests.map((guest) => (
                  <SelectItem key={guest.id} value={guest.id}>
                    {guest.full_name} {guest.phone && `(${guest.phone})`}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium">Check-in Date *</label>
            <Input
              type="date"
              value={formData.check_in_date}
              onChange={(e) => setFormData({ ...formData, check_in_date: e.target.value })}
              required
            />
          </div>
          <div>
            <label className="text-sm font-medium">Check-out Date *</label>
            <Input
              type="date"
              value={formData.check_out_date}
              onChange={(e) => setFormData({ ...formData, check_out_date: e.target.value })}
              min={formData.check_in_date}
              required
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium">Purpose</label>
            <Select
              value={formData.purpose}
              onValueChange={(value) => setFormData({ ...formData, purpose: value })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="accommodation">Accommodation</SelectItem>
                <SelectItem value="business">Business</SelectItem>
                <SelectItem value="conference">Conference</SelectItem>
                <SelectItem value="training">Training</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-sm font-medium">Payment Status</label>
            <Select
              value={formData.payment_status}
              onValueChange={(value) => setFormData({ ...formData, payment_status: value })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="partial">Partial</SelectItem>
                <SelectItem value="paid">Paid</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div>
          <label className="text-sm font-medium">Total Amount</label>
          <Input
            type="number"
            min="0"
            step="0.01"
            value={formData.total_amount}
            onChange={(e) => setFormData({ ...formData, total_amount: parseFloat(e.target.value) || 0 })}
            placeholder="0.00 (auto-calculated if 0)"
          />
        </div>

        <div>
          <label className="text-sm font-medium">Notes</label>
          <Textarea
            value={formData.notes}
            onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
            placeholder="Special requests or notes"
            rows={2}
          />
        </div>

        <div className="flex justify-end gap-2 pt-4 border-t">
          <Button type="button" variant="outline" onClick={onCancel}>Cancel</Button>
          <Button 
            type="submit" 
            disabled={isLoading || !formData.room_id || (!formData.guest_id && !useNewGuest)} 
            data-testid="submit-booking-btn"
          >
            {isLoading ? 'Creating...' : 'Create Booking'}
          </Button>
        </div>
      </form>
    );
  };

  return (
    <div className="space-y-6" data-testid="bookings-management">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold">Bookings</h2>
          <p className="text-sm text-gray-500">Manage room reservations and check-ins</p>
        </div>
        <Button 
          onClick={() => setShowAddDialog(true)} 
          disabled={availableRooms.length === 0}
          data-testid="add-booking-btn"
        >
          <Plus className="h-4 w-4 mr-2" />
          New Booking
        </Button>
      </div>

      {/* View Tabs */}
      <Tabs value={activeView} onValueChange={setActiveView}>
        <TabsList>
          <TabsTrigger value="active">Active</TabsTrigger>
          <TabsTrigger value="completed">Completed</TabsTrigger>
          <TabsTrigger value="cancelled">Cancelled</TabsTrigger>
        </TabsList>
      </Tabs>

      {isLoading ? (
        <div className="text-center py-8 text-gray-500">Loading bookings...</div>
      ) : bookings.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Calendar className="h-12 w-12 mx-auto mb-4 text-gray-300" />
            <h3 className="text-lg font-medium mb-2">No {activeView} bookings</h3>
            <p className="text-sm text-gray-500 mb-4">
              {activeView === 'active' ? 'Create a new booking to get started' : `No ${activeView} bookings found`}
            </p>
            {activeView === 'active' && availableRooms.length > 0 && (
              <Button onClick={() => setShowAddDialog(true)}>
                <Plus className="h-4 w-4 mr-2" />
                New Booking
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {bookings.map((booking) => (
            <Card key={booking.id} data-testid={`booking-card-${booking.id}`}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-lg bg-indigo-100 flex items-center justify-center">
                      <BedDouble className="h-6 w-6 text-indigo-600" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-medium">{booking.guest_name || 'Guest'}</h3>
                        <Badge className={statusColors[booking.status] || 'bg-gray-100'}>
                          {booking.status?.replace('_', ' ')}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-4 text-sm text-gray-500">
                        <span className="flex items-center gap-1">
                          <BedDouble className="h-3 w-3" />
                          Room {booking.room_number} • {booking.building_name}
                        </span>
                        {booking.guest_phone && (
                          <span>{booking.guest_phone}</span>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-6">
                    <div className="text-right">
                      <div className="flex items-center gap-1 text-sm">
                        <LogIn className="h-3 w-3 text-green-600" />
                        {new Date(booking.check_in_date).toLocaleDateString()}
                      </div>
                      <div className="flex items-center gap-1 text-sm">
                        <LogOut className="h-3 w-3 text-red-600" />
                        {new Date(booking.check_out_date).toLocaleDateString()}
                      </div>
                    </div>
                    
                    {booking.total_amount > 0 && (
                      <div className="text-right">
                        <p className="text-lg font-bold">${booking.total_amount}</p>
                        <Badge variant="outline" className="text-xs">
                          {booking.payment_status}
                        </Badge>
                      </div>
                    )}

                    {/* Action Buttons */}
                    {booking.status === 'confirmed' && (
                      <div className="flex gap-2">
                        <Button 
                          size="sm" 
                          onClick={() => { setSelectedBooking(booking); setActionType('checkin'); }}
                        >
                          <LogIn className="h-4 w-4 mr-1" />
                          Check In
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => { setSelectedBooking(booking); setActionType('cancel'); }}
                        >
                          <XCircle className="h-4 w-4" />
                        </Button>
                      </div>
                    )}
                    {booking.status === 'checked_in' && (
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => { setSelectedBooking(booking); setActionType('checkout'); }}
                      >
                        <LogOut className="h-4 w-4 mr-1" />
                        Check Out
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Add Booking Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>New Booking</DialogTitle>
          </DialogHeader>
          <BookingForm
            onSubmit={(data) => createMutation.mutate(data)}
            onCancel={() => setShowAddDialog(false)}
            isLoading={createMutation.isPending}
          />
        </DialogContent>
      </Dialog>

      {/* Check-in Confirmation */}
      <Dialog open={actionType === 'checkin'} onOpenChange={(open) => !open && setActionType(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Confirm Check-in</DialogTitle>
            <DialogDescription>
              Check in {selectedBooking?.guest_name} to Room {selectedBooking?.room_number}?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setActionType(null)}>Cancel</Button>
            <Button 
              onClick={() => checkInMutation.mutate(selectedBooking?.id)}
              disabled={checkInMutation.isPending}
            >
              {checkInMutation.isPending ? 'Processing...' : 'Check In'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Check-out Dialog */}
      <Dialog open={actionType === 'checkout'} onOpenChange={(open) => !open && setActionType(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Check Out Guest</DialogTitle>
            <DialogDescription>
              Check out {selectedBooking?.guest_name} from Room {selectedBooking?.room_number}?
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <label className="text-sm font-medium">Room Status After Checkout</label>
            <Select defaultValue="dirty">
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="dirty">Dirty (needs cleaning)</SelectItem>
                <SelectItem value="ready">Ready (no cleaning needed)</SelectItem>
                <SelectItem value="maintenance">Maintenance needed</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setActionType(null)}>Cancel</Button>
            <Button 
              onClick={() => checkOutMutation.mutate({ bookingId: selectedBooking?.id, roomStatus: 'dirty' })}
              disabled={checkOutMutation.isPending}
            >
              {checkOutMutation.isPending ? 'Processing...' : 'Check Out'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel Booking Dialog */}
      <Dialog open={actionType === 'cancel'} onOpenChange={(open) => !open && setActionType(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-red-600">Cancel Booking</DialogTitle>
            <DialogDescription>
              Are you sure you want to cancel this booking for {selectedBooking?.guest_name}?
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <label className="text-sm font-medium">Cancellation Reason</label>
            <Textarea placeholder="Optional reason for cancellation" rows={2} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setActionType(null)}>Keep Booking</Button>
            <Button 
              variant="destructive"
              onClick={() => cancelMutation.mutate({ bookingId: selectedBooking?.id, reason: '' })}
              disabled={cancelMutation.isPending}
            >
              {cancelMutation.isPending ? 'Cancelling...' : 'Cancel Booking'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BookingsManagement;
